import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';

import { Text, View } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useBalance } from '@app/hooks/user/mybalance';
import { useTradingWallets } from '@app/hooks/user/useTradingWallets';
import { Input } from 'antd';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import { useFetcherPublic } from '../../hooks/fetcher';
import CModalHeader from '../Modal/CModalHeader';
import TransferAmount from './TransferAmount';
import TransferCoin from './TransferCoin';
import TransferFooter from './TransferFooter';
import TransferFromTo from './TransferFromTo';
import { truncateWithComma } from '@/utils/Truncate';

const PRIMARY_COLOR = '#007AFF';
const PRIMARY_GRADIENT_DARK =
  'linear-gradient(135deg, #007AFF 0%, #33A7FF 50%, #6236FF 100%)';
const PRIMARY_GRADIENT_LIGHT =
  'linear-gradient(135deg, #007AFF 0%, #5EC6FF 50%, #B3E5FF 100%)';
const PRIMARY_GRADIENT_SIMPLE =
  'linear-gradient(90deg, #007AFF 0%, #33A7FF 100%)';

interface TransferModalProps {
  setShowModal: (show: boolean) => void;
  initialWallet?: string;
}

const TransferModal: React.FC<TransferModalProps> = ({
  setShowModal,
  initialWallet = 'spotwallet',
}) => {
  const { drawerBgColor } = useContext(ThemeContext).theme.COLORS;
  const { isDarkMode } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';
  const { t } = useTranslation();

  const [transferFrom, setTransferFrom] = useState(initialWallet);
  const [transferTo, setTransferTo] = useState(
    initialWallet === 'spotwallet'
      ? 'futurewallet'
      : initialWallet === 'futurewallet'
        ? 'spotwallet'
        : initialWallet === 'rewardwallet'
          ? 'futurewallet'
          : 'futurewallet'
  );
  const [amount, setAmount] = useState('');
  // const { data: tradeStore } = useTradeStore()
  let coinType = 'USDT';

  // transferFrom이 변경될 때 transferTo 자동 설정
  useEffect(() => {
    // 리워드지갑에서는 선물지갑으로만 전송 가능
    if (transferFrom === 'rewardwallet') {
      setTransferTo('futurewallet');
    }
    // 봇/카피 지갑에서는 현물 또는 선물지갑으로 전송
    else if (
      (transferFrom === 'botwallet' || transferFrom === 'copywallet') &&
      transferTo !== 'spotwallet' &&
      transferTo !== 'futurewallet'
    ) {
      setTransferTo('spotwallet');
    }
    // 현물지갑에서는 선물지갑으로 전송 (기본값)
    else if (
      transferFrom === 'spotwallet' &&
      (transferTo === 'spotwallet' || transferTo === 'rewardwallet')
    ) {
      setTransferTo('futurewallet');
    }
    // 선물지갑에서는 현물지갑으로 전송 (기본값)
    else if (
      transferFrom === 'futurewallet' &&
      (transferTo === 'futurewallet' || transferTo === 'rewardwallet')
    ) {
      setTransferTo('spotwallet');
    }
  }, [transferFrom]);

  // 지갑 유형에 따른 type 값 설정
  let type = 0;
  if (transferFrom === 'futurewallet') {
    type = 1;
  } else if (transferFrom === 'globalwallet') {
    coinType = 'KRW';
    type = 9;
  } else if (transferFrom === 'rewardwallet') {
    type = 7;
  }

  // 봇/카피 지갑 잔액 조회 (신규)
  const {
    data: tradingWallets,
    getBotAvailable,
    getCopyAvailable,
  } = useTradingWallets();

  // 선택된 지갑 유형과 코인에 따라 잔액을 조회 (기존 현물/선물/리워드 지갑)
  const { data: balance } = useBalance(coinType, type);

  // 선택된 지갑에 따라 잔액 가져오기
  const getWalletBalance = () => {
    if (transferFrom === 'botwallet') {
      return tradingWallets?.bot;
    } else if (transferFrom === 'copywallet') {
      return tradingWallets?.copy;
    }
    return balance;
  };

  const walletBalance = getWalletBalance();

  const currencyList = useFetcherPublic('/system/currency');

  const equivusd = currencyList?.data?.result?.result?.find(
    (item: any) => item?.code === 'KRW'
  );

  // 실제 표시할 잔액 계산
  const displayBalance = new BigNumber(walletBalance?.balance || 0)
    .minus(walletBalance?.lockbalance || 0)
    .toNumber();

  return (
    <ModernModalContainer isDarkMode={isDarkMode}>
      {/* <BackgroundGradient isDarkMode={isDarkMode} /> */}

      <ModalContent>
        <CModalHeader
          title={'transfer'}
          closeModal={() => setShowModal(false)}
        />

        <ContentWrapper>
          <SectionCard isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>
              {t('transferDirection')}
            </SectionTitle>
            <TransferFromTo
              transferFrom={transferFrom}
              setTransferFrom={setTransferFrom}
              transferTo={transferTo}
              setTransferTo={setTransferTo}
            />
          </SectionCard>

          <SectionCard isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>{t('coin')}</SectionTitle>
            <TransferCoin
              coinType={transferTo === 'globalwallet' ? 'KRW' : coinType}
            />
          </SectionCard>

          <SectionCard isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>{t('amount')}</SectionTitle>
            <TransferAmount
              balance={displayBalance}
              amount={amount}
              setAmount={setAmount}
              coinType={coinType}
            />

            <BalanceInfo isDarkMode={isDarkMode}>
              <BalanceLabel>{t('availableBalance')}</BalanceLabel>
              <BalanceValue>
                {new BigNumber(Math.max(displayBalance, 0)).toFormat(
                  4,
                  BigNumber.ROUND_DOWN
                )}{' '}
                USDT
              </BalanceValue>
            </BalanceInfo>
          </SectionCard>

          {(transferFrom === 'globalwallet' ||
            transferTo == 'globalwallet') && (
            <SectionCard isDarkMode={isDarkMode}>
              <SectionTitle isDarkMode={isDarkMode}>
                {t('transferamount') + ' : ' + t('currencyrate')} (
                {equivusd?.price})
              </SectionTitle>
              <CurrencyConversionInput
                isDarkMode={isDarkMode}
                readOnly
                value={
                  transferFrom === 'globalwallet'
                    ? BigNumber(amount || 0)
                        .dividedBy(equivusd?.price)
                        .toFormat(6) + ' USDT'
                    : BigNumber(amount || 0)
                        .multipliedBy(equivusd?.price)
                        .toFormat(0) + ' KRW'
                }
              />
            </SectionCard>
          )}

          <FooterWrapper>
            <TransferFooter
              transferFrom={
                transferFrom === 'spotwallet'
                  ? 0
                  : transferFrom === 'futurewallet'
                    ? 1
                    : transferFrom === 'globalwallet'
                      ? 9
                      : transferFrom === 'rewardwallet'
                        ? 7
                        : transferFrom === 'botwallet'
                          ? 'BOT'
                          : transferFrom === 'copywallet'
                            ? 'COPY'
                            : 0
              }
              transferTo={
                transferTo === 'spotwallet'
                  ? 0
                  : transferTo === 'futurewallet'
                    ? 1
                    : transferTo === 'globalwallet'
                      ? 9
                      : transferTo === 'rewardwallet'
                        ? 7
                        : transferTo === 'botwallet'
                          ? 'BOT'
                          : transferTo === 'copywallet'
                            ? 'COPY'
                            : 0
              }
              amount={amount}
              coinType={coinType}
              balance={displayBalance}
            />
          </FooterWrapper>
        </ContentWrapper>
      </ModalContent>
    </ModernModalContainer>
  );
};

// Styled Components
const ModernModalContainer = styled.div<{ isDarkMode: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: ${({ theme }) => theme.COLORS.ModalBg};
  border: 1px solid ${({ theme }) => theme.COLORS.ModalBorder};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 10px;
`;

const SectionCard = styled.div<{ isDarkMode: boolean }>`
  background: ${({ theme }) => theme.COLORS.SubmenuBg};
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.COLORS.SubmenuBorder};
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionTitle = styled.h3<{ isDarkMode: boolean }>`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.isDarkMode ? '#FFFFFF' : '#2C3E50')};
  letter-spacing: 0.5px;
  position: relative;
`;

const BalanceInfo = styled.div<{ isDarkMode: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background: ${(props) =>
    props.isDarkMode
      ? 'linear-gradient(135deg, rgba(0, 122, 255, 0.15) 0%, rgba(51, 167, 255, 0.08) 100%)'
      : 'linear-gradient(135deg, rgba(0, 122, 255, 0.12) 0%, rgba(51, 167, 255, 0.06) 100%)'};
  border-radius: 10px;
  border: 1px solid
    ${(props) =>
      props.isDarkMode ? 'rgba(0, 122, 255, 0.25)' : 'rgba(0, 122, 255, 0.18)'};
`;

const BalanceLabel = styled.span`
  font-size: 12px;
  color: #7f8c8d;
  font-weight: 500;
`;

const BalanceValue = styled.span`
  font-size: 14px;
  color: ${PRIMARY_COLOR};
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const CurrencyConversionInput = styled(Input)<{ isDarkMode: boolean }>`
  margin-top: 12px;
  background: ${(props) =>
    props.isDarkMode
      ? 'rgba(40, 40, 40, 0.8)'
      : 'rgba(50, 50, 50, 0.8)'} !important;
  border: 1px solid
    ${(props) =>
      props.isDarkMode
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(255, 255, 255, 0.2)'} !important;
  border-radius: 8px !important;
  padding: 12px 16px !important;
  font-weight: 600 !important;
  color: ${(props) => (props.isDarkMode ? '#FFFFFF' : '#E0E0E0')} !important;
  font-size: 14px !important;
`;

const FooterWrapper = styled.div`
  margin-top: auto;
  padding-top: 20px;
`;

export default React.memo(TransferModal);
